import * as React from 'react';
import { getLocalDateTime } from '../../Utils/GlobalUtils';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, Tooltip } from '@material-ui/core';
import { Results_NavLink_To } from '../../Constants/AppBar';
import { Link } from 'react-router-dom';
import DataGridAccordion from './ResultsDataGrid';
import { get, isEmpty, isNil } from 'lodash';
import { useState, useEffect } from 'react';
import AssetPreviewDrawer from './AssetPreviewDrawer';
import { getPrimarySrcFileUrl } from '../../Utils/AssetUtils';
import moment from 'moment-timezone';
import ManageAssetInNewTabLink from '../ManageAsset/ManageAssetInNewTabLink';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import AssetGridFilters from './AssetGridFilters';
import { getProductImgUrl } from '../../Utils/ProductUtils';
import ImageErrorAvatar from './ImageErrorAvatar';
import ModelRequiredAvatar from './ModelRequiredAvatar';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAssetGridData } from '../../redux/actions/resultsActions';

const useStyles = makeStyles((theme) => ({
	assetName: {
		'& p': {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			display: 'block',
			maxWidth: '250px',
		},
	},
	ppid: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: 'block',
		maxWidth: '180px',
	},
	tags: {
		display: 'block',
		'& a': {
			textDecoration: 'none',
			color: theme.palette.primary.main,
		},
	},
	avatar: {
		'& .MuiAvatar-root': {
			height: '3em',
		},
	},
}));

const Tags = ({ tags }) => {
	const classes = useStyles();
	// click on tag should start a new search in the same window.
	return (
		<div className={classes.tags}>
			{tags.map((tag, index) => {
				return (
					<Link key={tag} to={Results_NavLink_To + '/' + tag} color="primary">
						{(index ? ', ' : '') + tag}
					</Link>
				);
			})}
		</div>
	);
};

const getModifiedDuration = (timeStamp) => {
	const now = moment(new Date()); //todays date
	const end = moment.utc(timeStamp).local(); // another date
	const duration = moment.duration(now.diff(end));
	const days = duration.asDays().toFixed(0);
	const hours = duration.asHours().toFixed(0);
	const mins = duration.asMinutes().toFixed(0);
	let lastMod = `${days} days ago`;
	if (days === '0') {
		lastMod = `${hours} hours ago`;
	}
	if (hours === '0') {
		lastMod = `${mins} mins ago`;
	}
	return lastMod;
};
export default function AssetDataGrid({ searchTerm }) {
	const dispatch = useDispatch();
	const [openDrawer, setDrawerOpen] = React.useState(false);
	const [clickedAsset, setclickedAsset] = useState({});
	const asset3d = useSelector((state) => state.results.assets3dData);
	const assets = asset3d.data;
	const totalHits = asset3d.count;
	const [filteredAssets, setFilteredAssets] = useState(assets);
	const classes = useStyles();
	const [rowsState, setRowsState] = React.useState({
		page: 0,
		pageSize: 25,
		rows: filteredAssets,
		loading: false,
	});
	const assetFilters = useSelector((state) => state.results.assetFilters);
	const { isArchived, isBuyable, tags } = assetFilters;

	useEffect(() => {
		if (!isEmpty(searchTerm) && !isNil(searchTerm)) {
			const { page, pageSize } = rowsState;
			let active = true;
			// handle asset grid pagination fetching previous and next pages.
			(async () => {
				setRowsState((prev) => ({ ...prev, loading: true }));
				await dispatch(
					fetchAssetGridData(searchTerm, (page + 1) * pageSize, page * pageSize, isBuyable, isArchived, tags),
				);
				if (!active) {
					return;
				}
			})();
		}
		//eslint-disable-next-line
	}, [searchTerm, dispatch, rowsState.pageSize, rowsState.page, isBuyable, isArchived, tags]);
	// have a separate userEffect to  avoid infinite re-render when filtered assets changed.
	useEffect(() => {
		setRowsState((prev) => ({ ...prev, loading: false, rows: filteredAssets }));
	}, [filteredAssets]);

	const handleRowClick = (rowParams) => {
		setDrawerOpen(true);
		setclickedAsset(rowParams.row);
	};
	const AssetAvatar = ({ hasSrcFiles, imgUrl }) => {
		if (hasSrcFiles) {
			return <ImageErrorAvatar imgUrl={imgUrl} toolTipMsg="This model has a thumbnail image error" />;
		}
		return <ModelRequiredAvatar imgUrl="" label="Model Needed" toolTipMsg="This Asset needs a source model" />;
	};
	const AssetName = ({ asset }) => {
		return (
			<Grid container className={classes.assetName}>
				<Grid item>
					<Tooltip title={asset.name} placement="top" arrow={true}>
						<Typography>{asset.name}</Typography>
					</Tooltip>
				</Grid>
			</Grid>
		);
	};
	// Render count in red color when source or artifact files are 0.
	const FilesTypo = ({ len }) => {
		return <Typography color={len === 0 ? 'error' : 'textPrimary'}>{`${len} File(s)`}</Typography>;
	};
	// Grid columns
	const columns = [
		{
			field: 'Model',
			headerName: 'Model',
			width: 100,
			sortable: false,
			renderCell: (params) => {
				const asset = params.row;
				const imgUrl = getPrimarySrcFileUrl(asset);
				const hasSrcFiles = asset.sourceFiles.length > 0;
				return (
					<div className={classes.avatar}>
						<AssetAvatar hasSrcFiles={hasSrcFiles} imgUrl={imgUrl} />
					</div>
				);
			},
		},
		{
			field: 'Lowes.com',
			headerName: 'Lowes.com',
			width: 120,
			sortable: false,
			renderCell: (params) => {
				const asset = params.row;
				const product = asset.product;
				let isBuyable = true;
				if (!isNil(product) && !isEmpty(product)) {
					isBuyable = product.isBuyable.toLowerCase() === 'y' ? true : false;
				}
				const imgUrl = getProductImgUrl(asset);
				return (
					<div className={classes.avatar}>
						<ModelRequiredAvatar
							imgUrl={imgUrl}
							label="Product Needed"
							toolTipMsg="This asset needs an associated product"
							isProduct={true}
							disable={!isBuyable}
						/>
					</div>
				);
			},
		},
		{
			field: 'name',
			headerName: 'Name',
			width: 250,
			renderCell: (params) => {
				const row = params.row;
				return <AssetName asset={row} />;
			},
		},
		{
			field: 'id',
			headerName: 'PPID',
			width: 150,
			renderCell: (params) => {
				const assetUUID = params.value;
				return (
					<Tooltip title={assetUUID} placement="top" arrow={true}>
						<Typography className={classes.ppid}>{assetUUID}</Typography>
					</Tooltip>
				);
			},
		},
		{
			field: 'product',
			headerName: 'Product ID',
			width: 140,
			renderCell: (params) => {
				return <Typography>{get(params.value, 'omniItemId', '')}</Typography>;
			},
		},
		{
			field: 'tags',
			headerName: 'Tags',
			width: 250,
			sortable: false,
			renderCell: (params) => {
				const tags = params.value;
				return <Tags tags={tags} />;
			},
		},
		{
			field: 'sourceFiles',
			headerName: 'Sources',
			width: 95,
			sortable: false,
			renderCell: (params) => {
				return <FilesTypo len={params.value.length} />;
			},
		},
		{
			field: 'artifacts',
			headerName: 'Artifacts',
			width: 100,
			sortable: false,
			renderCell: (params) => {
				return <FilesTypo len={params.value.length} />;
			},
		},
		{
			field: 'updatedAt',
			headerName: 'Last Modified',
			width: 160,
			renderCell: (params) => {
				const timeStamp = params.value;
				const formattedDate = getLocalDateTime(timeStamp);
				return (
					<Tooltip title={formattedDate} arrow={true} placement="top">
						<Typography> {getModifiedDuration(timeStamp)}</Typography>
					</Tooltip>
				);
			},
		},
		{
			field: 'action',
			headerName: 'Action',
			width: 120,
			sortable: false,
			renderCell: (params) => {
				const assetUUID = params.row.id;
				return (
					<Tooltip title="view asset details page" placement="top" arrow={true}>
						<div>
							<ManageAssetInNewTabLink
								assetUUID={assetUUID}
								linkTxt="Manage"
								icon={<SettingsApplicationsIcon />}
							></ManageAssetInNewTabLink>
						</div>
					</Tooltip>
				);
			},
		},
	];
	const tempDrawerProps = {
		open: openDrawer,
		setOpen: setDrawerOpen,
		asset: clickedAsset,
	};
	return (
		<React.Fragment>
			<DataGridAccordion
				rows={filteredAssets}
				{...rowsState}
				columns={columns}
				label={'Assets'}
				totalHits={totalHits}
				filterComponent={
					<AssetGridFilters assets={assets} setFilteredAssets={setFilteredAssets} query={searchTerm} />
				}
				handleRowClick={handleRowClick}
				setRowState={setRowsState}
			/>
			<AssetPreviewDrawer {...tempDrawerProps}></AssetPreviewDrawer>
		</React.Fragment>
	);
}
